import MathInput from "components/inputs/mathInput"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ProductActionType, ProductActionWrite, TransactionType } from "types"
import useActions from "hooks/useActions"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import StockFormCallout from "components/callout/stockFormCallout"
import TextAreaInput from "components/inputs/textareaInput"
import useProducts from "hooks/useProducts"
import useTransactions from "hooks/useTransactions"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as RemoveIcon } from "assets/icons/remove.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import ProductSelector from "@/components/searchAndSelect/ProductSelector"
import TransactionSelector from "@/components/searchAndSelect/TransactionSelector"
import useStorages from "hooks/useStorages"
import { Link } from "react-router-dom"

interface RemoveFromProductStockFormProps {
    storageId: string
    productIdPreset?: string
}

const RemoveFromProductStockForm: React.FC<RemoveFromProductStockFormProps> = ({ storageId, productIdPreset }) => {

    const { t } = useTranslation()
    const { addToast, closeDialog, dialogData } = useLayout()
    const data = dialogData['remove_from_stock']
    // Opened from a transaction page: the action stays linked, and the transaction is not shown.
    const presetTransactionId = data?.transactionId || ''

    const [amount, setAmount] = useState(`0`)
    const [description, setDescription] = useState(``)
    const [productId, setProductId] = useState<string>('')
    const [selectedTransactionId, setSelectedTransactionId] = useState<string>(presetTransactionId)
    const [errors, setErrors] = useState<string[]>([])

    const {
        productsStatus,
        products
    } = useProducts({ storageId, productId })

    const { filteredTransactions, setTypeFilter } = useTransactions({ storageId })
    const { isActionsRequiredToBeLinkedToTransactions } = useStorages({ storageId })
    const requireTransaction = isActionsRequiredToBeLinkedToTransactions()

    const { decrementProductAsync, isDecrementing } = useActions({ storageId })

    // Filter for outgoing transactions only
    useEffect(() => {
        setTypeFilter('outgoing')
    }, [])

    // Grab provided productId.
    useEffect(() => {
        if (!productIdPreset) return
        setProductId(productIdPreset)
    }, [productIdPreset])

    // Grab transaction from dialog data if provided
    useEffect(() => {
        if (data && data.transactionId) {
            setSelectedTransactionId(data.transactionId)
        }
    }, [dialogData])

    const product = useMemo(() => products.find(i => i.id === productId), [products, productId])

    // Determine if there are any draft transactions (used to conditionally show selector)
    const hasDraftTransactions = useMemo(() => {
        if (!filteredTransactions) return false
        return filteredTransactions.some((transaction) => transaction.isDraft === true && transaction.isFinalized === false && transaction.isDeleted !== true)
    }, [filteredTransactions])

    const isValid = useMemo(() => {
        if (product?.status?.currentAmount === undefined) return
        if (requireTransaction && !selectedTransactionId) return false
        const num = Number(amount)
        if ( num > 0 ) {
            if ( product.stockSettings.allowNegativeInventory && product.status.currentAmount - num < 0 ) {
                return true
            } else if ( product.status.currentAmount - num >= 0 ) {
                return true
            } else {
                return false
            }
        } else {
            return false
        }
    }, [amount, product, requireTransaction, selectedTransactionId])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setErrors([])
        if ( amount === null || !isValid ) return
        if (requireTransaction && !selectedTransactionId) {
            setErrors(['ACTION_REQUIRES_TRANSACTION'])
            return
        }
        
        try {
            const actionData: ProductActionWrite = {
                type: ProductActionType.DECREMENT,
                ids: {
                    storageId,
                    productId
                },
                amount: Number(amount),
                description: description,
                ...(selectedTransactionId ? { transaction: { id: selectedTransactionId } } : {})
            }
            const result = await decrementProductAsync(actionData)
            addToast({ text: t('toastProductStockDecremented'), colorScheme: 'green' })
            closeDialog()
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
        }
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    if (productsStatus === `loading` && !product) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingProducts')}</Callout>

    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isDecrementing}
        >
            <Section>
            <Callout type="info" id="calloutRemoveProductsFromInventory"><p>{t('calloutRemoveProductsFromInventory')}</p></Callout>
            <div className={`input-holder-wide`}>
                <label>{t('selectProduct')}*</label>
                <ProductSelector 
                    storageId={storageId}
                    selectedProductCode={productId}
                    onProductSelect={setProductId}
                    placeholder={t('filterForIdOrName')}
                    inlineResults={true}
                />
            </div>
            {productId && product && product.status?.currentAmount !== undefined && (
                <>
                    <MathInput value={amount} setValue={setAmount} isRequired={true} negativeIsAllowed={false} label={t(`amount`)} />
                    <TextAreaInput label={t(`description`)} value={description} onChange={handleDescriptionChange} maxLength={200} />
                    {!presetTransactionId && (hasDraftTransactions || requireTransaction) && (
                        <div className="input-holder">
                            <label>{t('labelTransaction')}{requireTransaction ? '*' : ''}</label>
                            <TransactionSelector
                                storageId={storageId}
                                selectedTransactionId={selectedTransactionId}
                                onTransactionSelect={setSelectedTransactionId}
                                transactionType={TransactionType.OUTGOING}
                                draftOnly
                                placeholder={t(requireTransaction ? 'selectTransactionRequired' : 'selectTransaction', requireTransaction ? 'Select a transaction' : 'Select a transaction (optional)')}
                            />
                        </div>
                    )}
                    {!presetTransactionId && requireTransaction && !selectedTransactionId && !hasDraftTransactions && (
                        <Callout type="info">
                            <p>{t('calloutActionsRequireTransaction')}</p>
                            <Link to={`/storage/transactions/create/outgoing`}>{t('buttonNewOutgoing')}</Link>
                        </Callout>
                    )}
                </>
            )}
            <StockFormCallout product={product} amount={-Number(amount)} />
            <ButtonGroup>
                {isDecrementing ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="submit" design="primary" disabled={!isValid}><RemoveIcon /><span>{t('buttonRemoveFromStock')}</span></Button>
                )}
            </ButtonGroup>
            {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )

}

export default RemoveFromProductStockForm



